/**
 * Fuel Tracking Tab Component
 * Displays fuel consumption summary by equipment and fuel issue records
 */
import { Fuel, Edit, AlertTriangle } from "lucide-react";

const FuelTrackingTab = ({
  fuelSummary,
  fuelIssues,
  onViewAnalytics,
  onEditFuelIssue,
  isMasterAdmin
}) => {
  // Calculate average consumption and flag high consumers (>20% above avg)
  const avgConsumption = fuelSummary.length > 0 
    ? fuelSummary.reduce((s, eq) => s + (eq.total_fuel_consumed || 0), 0) / fuelSummary.length 
    : 0;
  const threshold = avgConsumption * 1.2;
  const highConsumers = fuelSummary.filter(eq => (eq.total_fuel_consumed || 0) > threshold);

  return (
    <div className="space-y-6">
      {/* Quick Analytics Summary Banner */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-sm p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/20 rounded-sm">
              <Fuel className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h4 className="font-heading font-semibold text-primary">Fuel Consumption Analytics</h4>
              <p className="text-sm text-slate-600">View detailed consumption reports, efficiency trends, and equipment-wise analysis</p>
            </div>
          </div>
          <button
            onClick={onViewAnalytics}
            className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-sm hover:bg-primary/90 transition-colors"
          >
            View Analytics
          </button>
        </div>
      </div>

      {/* High Consumption Alert */}
      {highConsumers.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-300 rounded-sm" data-testid="fuel-efficiency-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">High Fuel Consumption Alert</p>
              <p className="text-sm text-amber-700 mt-1">
                {highConsumers.length} machine(s) consuming &gt;20% above average ({avgConsumption.toFixed(0)}L):
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {highConsumers.map(eq => (
                  <span key={eq.equipment_id} className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded">
                    {eq.name}: {eq.total_fuel_consumed?.toFixed(0)}L
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Fuel Summary by Equipment */}
      <div className="stat-card">
        <h3 className="font-heading font-bold text-lg mb-4">Fuel Consumption by Equipment</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {fuelSummary.map((eq) => (
            <div key={eq.equipment_id} className="p-4 border border-slate-200 rounded-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-primary">{eq.name}</span>
                <span className="text-xs font-mono text-slate-500">{eq.registration}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-slate-500">Total Fuel</p>
                  <p className="font-mono font-semibold text-warning">{eq.total_fuel_consumed?.toFixed(0)} L</p>
                </div>
                <div>
                  <p className="text-slate-500">Avg Efficiency</p>
                  <p className="font-mono font-semibold">{eq.avg_fuel_efficiency?.toFixed(2)} L/hr</p>
                </div>
                <div>
                  <p className="text-slate-500">Total Hours</p>
                  <p className="font-mono font-semibold">{eq.total_hours?.toFixed(0)} hrs</p>
                </div>
                <div>
                  <p className="text-slate-500">Issues</p>
                  <p className="font-mono font-semibold">{eq.issue_count}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        {fuelSummary.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            <Fuel className="w-12 h-12 mx-auto mb-2 text-slate-400" />
            No fuel data available yet.
          </div>
        )}
      </div>

      {/* Fuel Issue Records */}
      <div className="stat-card overflow-x-auto">
        <h3 className="font-heading font-bold text-lg mb-4">Fuel Issue Records</h3>
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-slate-300">
              <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Issue No.</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Date</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Equipment</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Quantity</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Hour Reading</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">KM Reading</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Hrs/KM Run</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Efficiency</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Received By</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Log Linked</th>
              {isMasterAdmin && <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {fuelIssues.map((issue) => (
              <tr key={issue.id} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="py-3 px-4 font-mono text-sm font-medium text-warning">{issue.issue_number}</td>
                <td className="py-3 px-4 font-mono text-sm">{issue.date}</td>
                <td className="py-3 px-4">
                  <div>
                    <p className="font-medium">{issue.equipment_name}</p>
                    {issue.equipment_reg && (
                      <p className="text-xs font-mono text-slate-500">{issue.equipment_reg}</p>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4 text-right font-mono text-sm font-bold text-warning">{issue.quantity?.toFixed(0)} L</td>
                <td className="py-3 px-4 text-right font-mono text-sm">{issue.current_hour_reading ? issue.current_hour_reading.toFixed(0) : '-'}</td>
                <td className="py-3 px-4 text-right font-mono text-sm">{issue.current_km_reading ? issue.current_km_reading.toFixed(0) : '-'}</td>
                <td className="py-3 px-4 text-right font-mono text-sm">
                  {issue.km_run > 0 ? `${issue.km_run.toFixed(1)} km` : issue.km_or_hours_run > 0 ? `${issue.km_or_hours_run.toFixed(1)} hrs` : '-'}
                </td>
                <td className="py-3 px-4 text-right font-mono text-sm">
                  {issue.km_efficiency > 0 ? `${issue.km_efficiency.toFixed(2)} km/L` : issue.fuel_efficiency > 0 ? `${issue.fuel_efficiency.toFixed(2)} L/hr` : '-'}
                </td>
                <td className="py-3 px-4 text-sm">{issue.received_by}</td>
                <td className="py-3 px-4 text-sm">
                  {issue.daily_log_id ? (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Linked</span>
                  ) : <span className="text-slate-400">-</span>}
                </td>
                {isMasterAdmin && (
                  <td className="py-3 px-4 text-center">
                    <button onClick={() => onEditFuelIssue(issue)} className="p-1 hover:bg-slate-200 rounded" title="Edit" data-testid={`edit-fuel-${issue.id}`}>
                      <Edit className="w-4 h-4 text-primary" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {fuelIssues.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Fuel className="w-16 h-16 mx-auto mb-4 text-slate-400" />
            No fuel issues recorded yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default FuelTrackingTab;
